import { USD_DECIMALS } from '@funkit/connect-core'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated'

import { useHapticFeedback } from '../haptics/HapticsContext'
import { MOTION_EASING } from '../utils/motion'
import { type NumpadKey, applyNumpadKey } from '../utils/numpadInput'

// One shake is four half-cycles; the tint clears with the last of them.
const SHAKE_TRAVEL = 8
const SHAKE_STEP_DURATION = 50
const SHAKE_STEP = { duration: SHAKE_STEP_DURATION }
const WARNING_DURATION = SHAKE_STEP_DURATION * 4 * 2

// Typing a zero onto zero: the amount swells a touch and settles — quick up,
// slower home (Family: +8% in ~50ms, back in ~130ms).
const PULSE_SCALE = 1.08
const PULSE_UP = { duration: 50, easing: MOTION_EASING }
const PULSE_DOWN = { duration: 130, easing: MOTION_EASING }

/**
 * Not a limit anyone is meant to meet. Every real maximum — the host's, the
 * corridor's, the one a refusal reports — lets the value land and answers with
 * a message and a way back, because a buyer who asks for too much should be
 * told the number they can have. This one exists so the field cannot be filled
 * with an amount no maximum will ever be compared against.
 */
const ABSURD_AMOUNT_USD = 1_000_000

/**
 * The value sits over the source's ceiling — the floor's mirror, and read the
 * same way: a state the amount is allowed to hold while the screen names it.
 */
export function isAboveMaximum(value: string, limitUsd?: string): boolean {
  if (limitUsd === undefined) {
    return false
  }
  return Number(value) > Number(limitUsd)
}

/**
 * The value sits under the source's floor. Empty and zero are the untouched
 * state rather than a violation — there is nothing yet to be too small.
 */
export function isBelowMinimum(value: string, minLimitUsd?: string): boolean {
  if (minLimitUsd === undefined) {
    return false
  }
  const amount = Number(value)
  return amount > 0 && amount < Number(minLimitUsd)
}

/** `''` and `0` are one state: nothing entered yet. `0.`/`0.0` are typed and edit normally. */
function isZero(value: string): boolean {
  return value === '' || value === '0'
}

/**
 * An amount screen's input policy, separate from its layout. Every way a value
 * enters — a numpad key or a quick-amount chip — commits through this hook, so
 * the policy lives in exactly one place.
 *
 * `limitUsd` is the source's maximum, wherever it came from — the host's own
 * figure, the corridor's, or the one a refusal reports. The digit lands, the
 * amount jolts, and the screen names the bound and offers the way back, so the
 * buyer is always told the number they can have rather than left guessing at
 * a key that does nothing.
 *
 * Only a meaningless edit is refused outright besides — deleting from zero, or
 * any edit while `disabled` — and a zero typed onto zero is a no-op that swells
 * to acknowledge it.
 */
export function useGuardedAmountInput({
  input,
  onChangeInput,
  limitUsd,
  disabled = false,
}: {
  input: string
  onChangeInput: (input: string) => void
  /** The source's maximum; a value past it lands and the screen names it. */
  limitUsd?: string
  /** Locks the value: every numpad/chip commit rejects instead of applying. */
  disabled?: boolean
}) {
  const haptic = useHapticFeedback()
  const [hasWarning, setHasWarning] = useState(false)
  const warningTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(warningTimer.current), [])

  const shake = useSharedValue(0)
  const pulse = useSharedValue(1)
  const feedbackStyle = useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and esbuild
    // renames this one in the bundle (see AmountDisplay).
    'worklet'
    return {
      transform: [{ translateX: shake.value }, { scale: pulse.value }],
    }
  })

  // The amount swells and settles to acknowledge a value it did not get by
  // being typed — a zero onto zero, or a bound accepted from the action.
  const pulseAmount = useCallback(() => {
    pulse.value = withSequence(
      withTiming(PULSE_SCALE, PULSE_UP),
      withTiming(1, PULSE_DOWN),
    )
  }, [pulse])

  // The amount jolts side to side and tints for the length of it. Silent: this
  // is the answer to a value that *landed* outside a bound, and buzzing there
  // fires on every key once the bound is passed, which on a numpad is most of
  // them.
  const flashAmount = useCallback(() => {
    shake.value = withSequence(
      withTiming(-SHAKE_TRAVEL, SHAKE_STEP),
      withTiming(SHAKE_TRAVEL, SHAKE_STEP),
      withTiming(-SHAKE_TRAVEL / 2, SHAKE_STEP),
      withTiming(0, SHAKE_STEP),
    )
    setHasWarning(true)
    clearTimeout(warningTimer.current)
    warningTimer.current = setTimeout(
      () => setHasWarning(false),
      WARNING_DURATION,
    )
  }, [shake])

  // The same jolt for input that does not take at all. A refusal is one event
  // per press rather than one per key of a value still being typed, so the tap
  // it answers with is the difference between the two states rather than noise.
  const refuseAmount = useCallback(() => {
    haptic('error')
    flashAmount()
  }, [haptic, flashAmount])

  // The handlers close over the latest input through a ref rather than by
  // identity, so they stay stable across keystrokes — the keypad and chips are
  // memoised on them and would otherwise re-render 12+ animated views per key.
  const latest = useRef({ input, onChangeInput, limitUsd, disabled })
  latest.current = { input, onChangeInput, limitUsd, disabled }

  const commitInput = useCallback(
    (next: string) => {
      const {
        limitUsd: limit,
        onChangeInput: onChange,
        disabled: isDisabled,
      } = latest.current
      if (isDisabled) {
        refuseAmount()
        return
      }
      // Past the absurd bound the key simply does not take, and there is
      // nothing to name: no maximum was ever in play at this magnitude.
      if (Number(next) > ABSURD_AMOUNT_USD) {
        refuseAmount()
        return
      }
      // Over the source's maximum. The digit lands, the jolt acknowledges it,
      // and the screen carries the message and the way back.
      if (limit !== undefined && Number(next) > Number(limit)) {
        flashAmount()
      }
      onChange(next)
    },
    [flashAmount, refuseAmount],
  )

  const handleNumpadKey = useCallback(
    (key: NumpadKey) => {
      if (latest.current.disabled) {
        refuseAmount()
        return
      }
      const current = latest.current.input
      // `''` and `0` behave identically: nothing to delete or clear, and
      // another zero changes nothing — it just acknowledges the tap with a
      // swell.
      if ((key === 'backspace' || key === 'clear') && isZero(current)) {
        refuseAmount()
        return
      }
      if (key === '0' && isZero(current)) {
        pulseAmount()
        if (current === '') {
          // The zero is now typed, not a ghost — same glyph, brightened.
          latest.current.onChangeInput('0')
        }
        return
      }
      commitInput(applyNumpadKey(current, key, USD_DECIMALS))
    },
    [commitInput, refuseAmount, pulseAmount],
  )

  return {
    hasWarning,
    feedbackStyle,
    commitInput,
    handleNumpadKey,
    pulseAmount,
  }
}
